import React, { FC, useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useAtom } from 'jotai'
import { PinFill } from '../../../assets/icons'
import useSortMembersList from '../../../hooks/useSortMembersList'
import breakpoints from '../../../theme/breakpoints'
import { colors } from '../../../theme/palette'
import { baseButton, sortPlaceholder } from '../../../theme/reuseables'
import { membersAtom, getMemberWorkPlaces } from '../../../api/members/members'

import {
    CardContentMobile,
    CardHeader,
    CardHeaderCell,
    CardList,
    CardMobile,
} from '../../ui/Card'
import { SortButton } from '../../ui/SortButton'
import { Typography } from '../../ui/Typography/Typography'
import {
    workplacesPageAtom,
    allWorkplaces,
    filterSelected,
    searchWorkplaceAtom,
} from '../../../store/filter/filterAtom'
import { Button } from '../../ui/Button/Button'
import { Workplace } from '../../../models/Workplace'
import { EditWorkplaceNotes } from '../../modules/notes/EditWorkplaceNotes'
import { initialSelectedFilters } from '../../../models/Filter'
import { HeaderLinks } from '../../modules/Headerlinks'
import { Spinner } from '../../ui/Loaders/Spinner'
import { L, L_HTML } from '../../providers/Lokalise'
import SearchField from '../../ui/SearchField/SearchField'
import { WorkplaceCardCell } from './WorkplaceCardCell'

const useStyles = createUseStyles({
    '@keyframes fadeIn': {
        from: {
            opacity: 0,
        },
        to: {
            opacity: 1,
        },
    },
    container: {
        padding: '0 1.5rem',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                padding: '0',
            },
        },
    },
    headerArea: {
        display: 'flex',
        padding: '.5rem 0',
        minHeight: '5rem',
    },
    headerAreaTop: {
        display: 'flex',
        padding: '1rem 0 .5rem',
        borderBottom: `1px solid ${colors.borderColor}`,
        marginBottom: '1rem',
    },
    topArea: {
        borderBottom: `1px solid ${colors.gray700}`,
        // TODO: remove this when this area has been developed.
        minHeight: '60px',
    },
    headerActions: {
        display: 'flex',
        flexGrow: 1,
        marginLeft: 'auto',
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
        flexShrink: 0,
    },
    sendingEmailSettings: {
        display: 'flex',
        padding: '.5rem 1rem',
        minHeight: '5rem',
        width: '100%',
        backgroundColor: colors.green200,
        color: colors.black,
        justifyContent: 'space-between',
        alignItems: 'center',
        '& span': {
            fontWeight: 700,
        },
        animation: '.5s $fadeIn',
    },
    notes: {
        paddingTop: '20px',
    },
    dialogButtonsContainer: {
        display: 'flex',
        width: '100%',
        justifyContent: 'space-around',
    },
    button: {
        ...baseButton,
        ...{
            cursor: 'pointer',
        },
    },
    modalButtons: {
        display: 'flex',
    },
    modalCheckbox: {
        display: 'flex',
        position: 'absolute',
        right: '20px',
        top: '20px',
    },
    numnerOfNotes: {
        display: 'flex',
        padding: '30px 0 90px',
        justifyContent: 'center',
        alignItems: 'center',
    },
    iconWrapper: {
        position: 'relative',
    },
    noteContent: {
        display: '-webkit-box',
        WebkitLineClamp: '2',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    deleteButtons: {
        top: '0',
        right: '0',
        display: 'flex',
        zIndex: '1',
        position: 'absolute',
        flexWrap: 'wrap',
        transition: 'all .3s ease-in',
        justifyContent: 'end',
        width: '100%',
        height: '100%',
        alignItems: 'center',
        background: 'rgba(255,255,255, .7)',
    },
    sortPlaceholder: {
        ...sortPlaceholder,
    },
    googleLink: {
        marginLeft: 'auto',
    },
    showMembers: {
        display: 'flex',
    },
    link: {
        fontWeight: '400',
        fontSize: '16px',
        textDecoration: 'underline',
        color: colors.black,
        '&:visited, &:active, &:focus': {
            color: colors.black,
        },
        '&:hover': {
            color: colors.green500,
        },
    },
    numberOfMembers: {
        fontSize: '16px',
        color: colors.black,
        marginRight: '20px',
    },
    contentMobile: {
        padding: '20px',
    },
    searchField: {
        width: '100%',
        marginRight: '20px',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                width: 'auto',
                minWidth: '425px',
                marginRight: '20px',
            },
        },
    },
})

const gridTemplateColumns = '23% 20% auto 230px'

const Workplaces: FC = () => {
    const [loading, setLoading] = useState(true)
    const classes = useStyles()
    const { setSort, sortState } = useSortMembersList()
    const [sortedWorkplaces] = useAtom(workplacesPageAtom)
    const [, setSearchAtom] = useAtom(searchWorkplaceAtom)
    const [, setAllWorkplaces] = useAtom(allWorkplaces)
    const [, setFilterSelected] = useAtom(filterSelected)
    const [membersData] = useAtom(membersAtom)

    const init = (): void => {
        async function fetchData(): Promise<void> {
            setSort('name')
            const res = await getMemberWorkPlaces()
            setAllWorkplaces(res.workplaces)

            setLoading(false)
        }

        fetchData()
    }

    useEffect(() => {
        if (!sortState) {
            setSort('date')
        }
        init()

        return () => {
            setFilterSelected(initialSelectedFilters)
        }
    }, [])

    const getNumberOfMembers = (workPlace: Workplace): number => {
        let numberOfMembers = []

        workPlace.trMemberList.forEach((item) => {
            numberOfMembers = [...numberOfMembers, ...item.Medlemsidenter]
        })

        const members = Array.from(new Set(numberOfMembers))

        return members.length
    }

    const onClickMapLink = (wp: Workplace): void => {
        // eslint-disable-next-line max-len
        const link = `https://www.google.com/maps/search/?api=1&query=${wp.address.addressLine1}+${wp.address.addressLine2}+${wp.address.addressLine3}%2C+${wp.address.postNumber}+${wp.address.postDistrict}`

        window.open(link, '_blank')
    }

    const onSearch = (search: string): void => {
        setSearchAtom(search)
    }

    const useSearch = (search: string): void => {
        setSearchAtom(search)
    }

    if (loading) return <Spinner />

    return (
        <div className={`${classes.container} page-up`}>
            <div className={`${classes.headerAreaTop} desktop-only`}>
                <div className={classes.headerActions}>
                    <HeaderLinks />
                </div>
            </div>
            <div className={classes.headerArea}>
                <div className="desktop-only">
                    <Typography tag="h1" size="XXXXL">
                        Mine arbejdssteder
                    </Typography>
                </div>
            </div>
            <div className={classes.headerArea}>
                <Typography tag="p" size="S" color={colors.gray900}>
                    {L_HTML('MINEARBEJDSSTEDER_ARKIVEREDE_OVERSKRIFT_TEXT')}
                </Typography>
            </div>
            <div style={{ marginTop: '10px' }}>
                <div className={classes.headerActions}>
                    <div className={classes.searchField}>
                        <SearchField
                            autoFocus
                            label={L('MINEARBEJDSSTEDER_SØGEFELT_LABEL')}
                            required={false}
                            typeSearch="primary"
                            searchButtonLabel={L(
                                'MINEARBEJDSSTEDER_SØGEFELT_LABEL'
                            )}
                            onSearch={onSearch}
                            onChange={useSearch}
                            // style={{ minWidth: '425px', marginRight: '20px' }}
                        />
                    </div>
                </div>
            </div>
            <div className={`${classes.notes}`}>
                {sortedWorkplaces && sortedWorkplaces.length ? (
                    <CardList>
                        <CardHeader gridTemplateColumns={gridTemplateColumns}>
                            <CardHeaderCell>
                                <SortButton
                                    onClick={() => setSort('name')}
                                    sort={sortState}
                                    name="name"
                                >
                                    Arbejdssted
                                </SortButton>
                            </CardHeaderCell>
                            <CardHeaderCell>
                                <SortButton
                                    onClick={() => setSort('numberOfMembers')}
                                    sort={sortState}
                                    name="numberOfMembers"
                                >
                                    Medlemmer
                                </SortButton>
                            </CardHeaderCell>
                            <CardHeaderCell>
                                <div className={classes.sortPlaceholder}>
                                    Adresse
                                </div>
                            </CardHeaderCell>
                            <CardHeaderCell>
                                <SortButton
                                    onClick={() => setSort('notes')}
                                    sort={sortState}
                                    name="notes"
                                >
                                    <span style={{ paddingLeft: '18px' }}>
                                        Noter
                                    </span>
                                </SortButton>
                            </CardHeaderCell>
                        </CardHeader>
                        {sortedWorkplaces.map((wp) => (
                            <div key={wp.number}>
                                <CardMobile gridTemplateColumns="auto">
                                    <div className={classes.contentMobile}>
                                        <CardContentMobile>
                                            <Typography
                                                tag="h3"
                                                size="XS"
                                                spacing=".2rem"
                                                color="rgba(0, 0, 0, 0.9)"
                                            >
                                                {wp.name}
                                            </Typography>
                                            <Typography
                                                size="XS"
                                                spacing=".2rem"
                                                color="rgba(0, 0, 0, 0.6)"
                                            >
                                                <Typography size="XS">
                                                    {wp.address.addressLine1},{' '}
                                                    {wp.address.postNumber}{' '}
                                                    {wp.address.postDistrict}
                                                </Typography>
                                            </Typography>
                                            <Button
                                                type="action"
                                                style={{
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() =>
                                                    onClickMapLink(wp)
                                                }
                                            >
                                                <div
                                                    style={{ display: 'flex' }}
                                                >
                                                    <Typography
                                                        size="XS"
                                                        style={{
                                                            textDecoration:
                                                                'underline',
                                                        }}
                                                    >
                                                        Vis vej
                                                    </Typography>
                                                    <PinFill
                                                        width="20px"
                                                        color={colors.green500}
                                                    />
                                                </div>
                                            </Button>
                                            <EditWorkplaceNotes
                                                workplace={wp}
                                            />
                                        </CardContentMobile>
                                    </div>
                                </CardMobile>
                                <WorkplaceCardCell
                                    wp={wp}
                                    allMembers={membersData.members}
                                />
                            </div>
                        ))}
                    </CardList>
                ) : null}
            </div>
            <div className={classes.numnerOfNotes}>
                <Typography size="XS">
                    Viser {sortedWorkplaces.length} arbejdssteder
                </Typography>
            </div>
        </div>
    )
}

export { Workplaces }
